import type { ChartColumn } from './types';

interface DataTableProps {
  columns: ChartColumn[];
  rows: Record<string, unknown>[];
  title?: string;
}

function formatCell(value: unknown, type: ChartColumn['type']): string {
  if (value === null || value === undefined) return '—';
  if (type === 'percent') return `${(value as number).toFixed(1)}%`;
  if (type === 'number') return (value as number).toLocaleString();
  return String(value);
}

/**
 * Scrollable table for row-based widget data. Renders `'—'` for null/undefined cells.
 * `title` renders as `<caption>` for screen readers but is visually hidden.
 *
 * @example
 * <DataTable
 *   title="Member performance"
 *   columns={[{ key: 'name', label: 'Member', type: 'string' }, { key: 'tasks', label: 'Tasks', type: 'number' }]}
 *   rows={[{ name: 'Alice', tasks: 23 }, { name: 'Bob', tasks: 17 }]}
 * />
 */
export function DataTable({ columns, rows, title }: DataTableProps) {
  return (
    <div className="h-full overflow-auto">
      <table className="min-w-full table-auto text-sm">
        {title && <caption className="sr-only">{title}</caption>}
        <thead>
          <tr className="border-b">
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className="whitespace-nowrap px-2 py-1.5 text-left text-xs font-medium text-muted-foreground"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b last:border-0 hover:bg-muted/40">
              {columns.map((col) => (
                <td key={col.key} className="px-2 py-1.5 text-sm">
                  {formatCell(row[col.key], col.type)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
